import { EventEmitter, Injectable, signal } from '@angular/core';
import { toObservable, toSignal } from '@angular/core/rxjs-interop';
import { faker } from '@faker-js/faker';
import { BehaviorSubject, combineLatest, map, scan, shareReplay, startWith, tap } from 'rxjs';
import * as io from 'socket.io-client';

import { ChatMessage, Course, environment, DocumentSection, User, UpdateChatMessage } from 'src/index';

@Injectable({
    providedIn: 'root'
})
export class ChatService {
    chatInitialState = false;

    toggleChat$ = new EventEmitter<boolean | undefined>(this.chatInitialState);
    chatShown$ = this.toggleChat$.pipe(
        scan((currentState: boolean, val: boolean | undefined) => val === undefined ? !currentState : val, this.chatInitialState),
        startWith(this.chatInitialState),
        shareReplay(1),
        map(shown => !environment.hideChat && shown),
        tap((shown) => this.handleChatBackdrop(shown)),
    );
    $chatShown = toSignal(this.chatShown$);
    $currentCourse = signal<Course | undefined>(undefined);
    $isGlobal = signal<boolean>(true);

    socketClient: any = io;

    sockets: Map<string, any> = new Map<string, any>();

    currentUser: User | undefined;
    $currentChatIds = signal<string[]>([]);
    $fetchedChatMessages = signal<Map<string, ChatMessage[]>>(new Map<string, ChatMessage[]>());

    $notifications = signal<Map<string, number>>(new Map<string, number>());
    $currentChatPeopleOnline = signal<Map<string, number>>(new Map<string, number>());

    getTotalNotifications(): number {
        const values = this.$notifications().values();
        let total = 0;
        let next = values.next();
        while (next.value) {
            total += next.value;
            next = values.next();
        }
        return total;
    }

    initSocket(roomId: string) {
        if (environment.chatSocketUrl) {
            if (environment.chatSocketUrl.indexOf('wss') > -1) {
                const uriConnect = environment.chatSocketUrl.replace('/chat', '');
                this.sockets.set(roomId, this.socketClient.connect(uriConnect, { path: '/chat/socket.io', transports: ['websocket'] }));
            } else {
                this.sockets.set(roomId, this.socketClient.connect(environment.baseUrlApi, { path: environment.chatSocketUrl, transports: ['websocket'] }));
            }
        }
    }

    joinRooms(user: User, roomIds: string[]) {
        roomIds.forEach(roomId => {
            if (!this.sockets.get(roomId)) {
                this.initSocket(roomId);
                const roomSocket = this.sockets.get(roomId);
                if (roomSocket.disconnected) {
                    roomSocket.on('connect', () => {
                        roomSocket.on('connected', (data: any) => {
                            this.$currentChatIds.update(ids => {
                                if (data.room && ids.indexOf(data.room) === -1) {
                                    ids.push(data.room);
                                }
                                return ids;
                            });
                        });
                        roomSocket.on('fetch_messages', (data: any) => {
                            this.$fetchedChatMessages().set(roomId, data?.messages || []);
                        });
                        roomSocket.on('people_count_updated', (data: any) => {
                            this.$currentChatPeopleOnline().set(roomId, data?.count || 0);
                        });
                        roomSocket.on('reconnect', () => {
                            if (this.$currentChatIds().length > 0 && this.currentUser) {
                                this.$currentChatIds().forEach(roomId => this.joinToRoom(user, roomId));
                            }
                        });
                        roomSocket.on('new_message', (data: ChatMessage) => {
                            if (data) {
                                if (data.room) {
                                    const currentValue = this.$notifications().get(data.room);
                                    this.$notifications().set(data.room, (currentValue ? currentValue : 0) + 1);
                                    this.$fetchedChatMessages().set(data.room, [data, ...this.$fetchedChatMessages().get(data.room) || []]);
                                } else {
                                    const currentValue = this.$notifications().get('EMPTY_DATA_ROOM');
                                    this.$notifications().set('EMPTY_DATA_ROOM', (currentValue ? currentValue : 0) + 1);
                                    this.$fetchedChatMessages().set('EMPTY_DATA_ROOM', [data, ...this.$fetchedChatMessages().get('EMPTY_DATA_ROOM') || []]);
                                }
                            }
                        });
                        if (this.$currentChatIds().indexOf(roomId) === -1) {
                            this.joinToRoom(user, roomId);
                        }

                    });
                } else if (roomSocket && !roomSocket.disconnected) {
                    if (this.$currentChatIds().indexOf(roomId) === -1) {
                        this.joinToRoom(user, roomId);
                    }
                }
            }
        });
    }

    joinToRoom(user: User, roomId: string) {
        if (this.sockets.get(roomId)) {
            this.sockets.get(roomId).emit('join_to_room', {
                username: user.username,
                user: `${user.firstName} ${user.lastName}`,
                room: roomId,
            });
        }
    }

    updateMessage(roomId: string, message: UpdateChatMessage) {
        if (this.sockets.get(roomId)) {
            this.sockets.get(roomId).emit('update_message', message);
        }
    }

    closeRoom() {
        this.$currentChatIds().forEach(roomId => {
            if (this.sockets.get(roomId)) {
                this.sockets.get(roomId).on('disconnect', () => this.sockets.set(roomId, undefined));
                this.sockets.get(roomId).disconnect();
            }
        });
        this.$currentChatIds.set([]);
        this.$fetchedChatMessages.set(new Map<string, ChatMessage[]>());
    }

    sendMessage(roomId: string | undefined, user: User, message: string) {
        if (roomId && this.sockets.get(roomId)) {
            const timestamp = new Date().getTime();
            this.sockets.get(roomId).emit('new_message', {
                message,
                timestamp,
            });
        }
    }

    handleChatBackdrop(shown: boolean) {
        if (shown) {
            document.body.classList.add('bg-black-overlay-chat');
        } else {
            document.body.classList.remove('bg-black-overlay-chat');
        }
    }

    getTitle() {
        return 'Code of Conduct';
    }

    getCodeOfConductText(): DocumentSection[] {
        // TODO: Costruire testo in base a funzionalità attive per il progetto
        return [{
            title: 'Lorem ipsum dolor sit amet, consectetur adipiscing elit',
            text: 'Sed do eiusmod tempor incididunt ut labore et dolore magna aliqua. Ut enim ad minim veniam, quis nostrud exercitation ullamco laboris nisi ut aliquip ex ea commodo consequat. Duis aute irure dolor in reprehenderit in voluptate velit esse cillum dolore eu fugiat nulla pariatur. Excepteur sint occaecat cupidatat non proident, sunt in culpa qui officia deserunt mollit anim id est laborum.',
            type: 'text',
        }, {
            title: 'Curabitur pretium tincidunt lacus.',
            text: 'Nulla gravida orci a odio. Nullam varius, turpis et commodo pharetra, est eros bibendum elit, nec luctus magna felis sollicitudin mauris. Integer in mauris eu nibh euismod gravida. Duis ac turpis nulla. <strong>Sed vel erat ut leo aliquet tempor.</strong> Suspendisse commodo urna sit amet quam tristique, ac feugiat velit molestie. Vivamus quis arcu auctor, fermentum erat ac, laoreet ante. Donec mollis est ac orci suscipit, nec auctor ipsum vulputate. Mauris tincidunt, justo ac lacinia mollis, velit risus tempor est, quis suscipit lorem ante ac ante.',
            type: 'text',
        }, {
            title: 'Donec et gravida elit.',
            description: 'Sed do eiusmod tempor incididunt ut labore et dolore magna aliqua.',
            list: [{
                title: 'Nulla gravida orci a odio.',
                text: 'Aliquam erat volutpat. Mauris sit amet risus id odio dictum suscipit. Cras ut erat vitae tortor porttitor sollicitudin. Nam tempus, felis eu viverra condimentum, felis felis convallis augue, ac auctor nunc risus a leo. <strong>Phasellus convallis purus sed felis placerat, vel feugiat sapien malesuada.</strong>',
            },
            {
                text: 'Aliquam erat volutpat. Mauris sit amet risus id odio dictum suscipit. Cras ut erat vitae tortor porttitor sollicitudin. Nam tempus, felis eu viverra condimentum, felis felis convallis augue, ac auctor nunc risus a leo. <strong>Phasellus convallis purus sed felis placerat, vel feugiat sapien malesuada.</strong>',
            },
            {
                title: 'Sed do eiusmod tempor incididunt ut labore et dolore magna aliqua.',
                text: 'Aliquam erat volutpat. Mauris sit amet risus id odio dictum suscipit. Cras ut erat vitae tortor porttitor sollicitudin. Nam tempus, felis eu viverra condimentum, felis felis convallis augue, ac auctor nunc risus a leo. <strong>Phasellus convallis purus sed felis placerat, vel feugiat sapien malesuada.</strong>',
            }],
            type: 'list',
            symbol: 'dot',
        }, {
            title: 'Donec et gravida elit.',
            list: [{
                title: 'Sed do eiusmod tempor incididunt ut labore et dolore magna aliqua.',
                text: 'Aliquam erat volutpat. Mauris sit amet risus id odio dictum suscipit. Cras ut erat vitae tortor porttitor sollicitudin. Nam tempus, felis eu viverra condimentum, felis felis convallis augue, ac auctor nunc risus a leo. <strong>Phasellus convallis purus sed felis placerat, vel feugiat sapien malesuada.</strong>',
            },
            {
                text: 'Aliquam erat volutpat. Mauris sit amet risus id odio dictum suscipit. Cras ut erat vitae tortor porttitor sollicitudin. Nam tempus, felis eu viverra condimentum, felis felis convallis augue, ac auctor nunc risus a leo. <strong>Phasellus convallis purus sed felis placerat, vel feugiat sapien malesuada.</strong>',
            },
            {
                title: 'Sed do eiusmod tempor incididunt ut labore et dolore magna aliqua.',
                text: 'Aliquam erat volutpat. Mauris sit amet risus id odio dictum suscipit. Cras ut erat vitae tortor porttitor sollicitudin. Nam tempus, felis eu viverra condimentum, felis felis convallis augue, ac auctor nunc risus a leo. <strong>Phasellus convallis purus sed felis placerat, vel feugiat sapien malesuada.</strong>',
            }],
            type: 'list',
            symbol: 'index',
        }, {
            title: 'Lorem ipsum dolor sit amet, consectetur adipiscing elit',
            text: 'Sed do eiusmod tempor incididunt ut labore et dolore magna aliqua. Ut enim ad minim veniam, quis nostrud exercitation ullamco laboris nisi ut aliquip ex ea commodo consequat. Duis aute irure dolor in reprehenderit in voluptate velit esse cillum dolore eu fugiat nulla pariatur. Excepteur sint occaecat cupidatat non proident, sunt in culpa qui officia deserunt mollit anim id est laborum.',
            type: 'text',
        }, {
            title: 'Curabitur pretium tincidunt lacus.',
            text: 'Nulla gravida orci a odio. Nullam varius, turpis et commodo pharetra, est eros bibendum elit, nec luctus magna felis sollicitudin mauris. Integer in mauris eu nibh euismod gravida. Duis ac turpis nulla. <strong>Sed vel erat ut leo aliquet tempor.</strong> Suspendisse commodo urna sit amet quam tristique, ac feugiat velit molestie. Vivamus quis arcu auctor, fermentum erat ac, laoreet ante. Donec mollis est ac orci suscipit, nec auctor ipsum vulputate. Mauris tincidunt, justo ac lacinia mollis, velit risus tempor est, quis suscipit lorem ante ac ante.',
            type: 'text',
        }, {
            title: 'Donec et gravida elit.',
            text: 'Aliquam erat volutpat. Mauris sit amet risus id odio dictum suscipit. Cras ut erat vitae tortor porttitor sollicitudin. Nam tempus, felis eu viverra condimentum, felis felis convallis augue, ac auctor nunc risus a leo. <strong>Phasellus convallis purus sed felis placerat, vel feugiat sapien malesuada.</strong>',
            type: 'text',
        }];
    }
}